import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether the line this selector belongs to may be removed from the cart.
   * Mirrors `item.instructions.can_remove`, which the Liquid emits as
   * `data-can-remove="false"` for lines an app or bundle manages.
   * @returns {boolean}
   */
  get canRemoveLine() {
    return this.dataset.canRemove !== 'false';
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are otherwise managed client-side, but a line the Liquid
   * rendered as disabled (`can_update_quantity: false`, i.e. an app or bundle
   * owns the quantity) stays disabled — the base class captured that in
   * `serverDisabled*` on connect.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Unlike the product form, the cart's minus button stays enabled once the
    // quantity is down to the minimum — from there it removes the line (see
    // decreaseQuantity). It only goes back to disabled when the line can't be
    // removed at all.
    minusButton.disabled = this.serverDisabledMinus || (value <= min && !this.canRemoveLine);
    plusButton.disabled = this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Handles the quantity decrease event.
   * Stepping below the minimum removes the line instead of clamping to it.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    // Belt and braces against a programmatic dispatch: a server-disabled minus
    // never fires a real click, but nothing else stops a line the merchant's
    // app locked from being decremented — or removed — from script.
    if (this.serverDisabledMinus) return;

    const { min, step, value } = this.getCurrentValues();

    if (value - step >= min) {
      this.updateQuantity(-1);
      return;
    }

    if (!this.canRemoveLine) return;

    // A removal, not a decrement: cart-items-component turns a quantity of 0
    // into onLineItemRemove, which owns the row animation and the empty-cart
    // transition. The input is deliberately left alone so a failed request
    // doesn't strand a 0 in the field.
    const { quantityInput } = this.refs;
    this.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine) || undefined));
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
